import React from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/contexts/SettingsContext";
import { Link, useParams } from "react-router-dom";
import { Section, Overline } from "@/components/Primitives";

const LEGAL_PAGES = ["mentions", "cgv", "cgu", "privacy", "cookies", "accessibility", "sitemap"];

const ROUTES = [
  { to: "/", key: "home" }, { to: "/restaurant", key: "restaurant" }, { to: "/menu", key: "menu" },
  { to: "/gallery", key: "gallery" }, { to: "/reservation", key: "reservation" }, { to: "/events", key: "events" },
  { to: "/reviews", key: "reviews" }, { to: "/blog", key: "blog" }, { to: "/contact", key: "contact" },
];

function getContent(slug, t, s) {
  const ph = "À compléter";
  switch (slug) {
    case "mentions":
      return `**${t("legal.mentions_title")}**\n\nÉditeur : ${s?.legal_company || ph}\nSIRET : ${s?.legal_siret || ph}\nAdresse : ${s?.address || ph}\nTéléphone : ${s?.phone || ph}\nEmail : ${s?.email || ph}\nDirecteur de la publication : ${s?.legal_director || ph}\nHébergeur : ${s?.legal_host || ph}\n\nCréation du site : Digital Corp France.`;
    case "cgv":
      return `**${t("legal.cgv_title")}**\n\nRéservations gratuites. Pas de paiement en ligne.\n\nConditions d'annulation : merci de prévenir le restaurant au moins 24h à l'avance par téléphone ou WhatsApp.\n\nÉvénements privés / devis : conditions particulières communiquées sur demande. Un acompte peut être demandé pour la privatisation.\n\nClauses modifiables sans préavis. Pour toute question : ${s?.email || "contact@restaurantle17.com"}.`;
    case "cgu":
      return `**${t("legal.cgu_title")}**\n\nUtilisation du site : ce site est destiné à présenter le restaurant Le 17 et à permettre la prise de contact / réservation.\n\nFormulaires : les informations transmises sont utilisées uniquement pour traiter votre demande.\n\nResponsabilité utilisateur : vous vous engagez à utiliser ce site dans le respect des lois en vigueur.\n\nPropriété intellectuelle : l'ensemble des contenus (textes, photos, vidéos, logo) sont la propriété de Le 17 ou de leurs auteurs respectifs et ne peuvent être reproduits sans autorisation.\n\nRestrictions : tout usage commercial ou frauduleux est interdit.`;
    case "privacy":
      return `**${t("legal.privacy_title")} / RGPD**\n\nDonnées collectées : nom, prénom, téléphone, email, date, heure, nombre de personnes, message.\n\nFinalités : gestion des réservations, demandes d'événements, contact client.\n\nDurée de conservation : 24 mois maximum après la dernière interaction.\n\nDroits : vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition. Pour exercer ces droits, écrivez à ${s?.email || ph}.\n\nAucune revente de vos données. Aucun partage avec des tiers à des fins commerciales.\n\nContact RGPD : ${s?.email || ph}.`;
    case "cookies":
      return `**${t("legal.cookies_title")}**\n\nCe site utilise des cookies pour améliorer votre expérience.\n\n- Cookies nécessaires : indispensables au fonctionnement du site.\n- Cookies analytiques : déposés uniquement avec votre consentement explicite.\n\nVous pouvez accepter, refuser ou personnaliser votre choix à tout moment via la bannière cookies, ou en effaçant le stockage local de votre navigateur.\n\nConforme au RGPD.`;
    case "accessibility":
      return `**${t("legal.accessibility_title")}**\n\nNous nous engageons à rendre ce site accessible au plus grand nombre :\n\n- Contrastes lisibles entre le texte et le fond\n- Navigation possible au clavier\n- Texte alternatif sur les images significatives\n- Site responsive (mobile, tablette, ordinateur)\n- Hiérarchie de titres claire\n\nSi vous rencontrez une difficulté, merci de nous le signaler à ${s?.email || ph}.`;
    case "sitemap":
      return "_SITEMAP_";
    default:
      return "";
  }
}

export default function Legal() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { settings: s } = useSettings();

  const titleKey = `legal.${slug}_title`;
  const content = getContent(slug, t, s);

  return (
    <div className="pt-32" data-testid={`legal-${slug}-page`}>
      <Section>
        <Overline>{t("footer.legal")}</Overline>
        <h1 className="font-serif-luxe text-4xl sm:text-5xl font-light text-[#F8F5EF] mt-3 mb-10">{t(titleKey)}</h1>

        {content === "_SITEMAP_" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl">
            {ROUTES.map(r => (
              <Link key={r.key} to={r.to} className="luxe-card p-5 hover:border-[#D4AF37]/50" data-testid={`sitemap-${r.key}`}>
                <div className="overline mb-1">·</div>
                <div className="text-[#F8F5EF]">{t(`nav.${r.key}`)}</div>
              </Link>
            ))}
            {LEGAL_PAGES.map(p => (
              <Link key={p} to={`/legal/${p}`} className="luxe-card p-5 hover:border-[#D4AF37]/50">
                <div className="overline mb-1">·</div>
                <div className="text-[#F8F5EF]">{t(`footer.links.${p}`)}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="max-w-3xl text-[#F8F5EF]/80 leading-relaxed whitespace-pre-line text-base">{content.split(/\*\*(.+?)\*\*/).map((part, i) => i % 2 === 1 ? null : part).join("")}</div>
        )}
      </Section>
    </div>
  );
}
